import React from 'react';
import { Spin } from 'antd';
import { FileOutlined } from '@ant-design/icons';
import { getVideoPreviewUrl } from '../../api/video';
import { acquirePreviewSlot } from '../../utils/previewLoadQueue';
import { subscribePreviewPause, subscribePreviewResume } from '../../utils/previewPauseBus';
import {
    captureVideoFrame,
    getVideoThumb,
    setVideoThumb,
} from '../../utils/videoThumbCache';

/** 预览定格位置（秒）：固定 1s，避免长视频 seek 到 8% 再拉一大段数据 */
const PREVIEW_SEEK_SEC = 1;

function needsCrossOrigin(url) {
    if (!url || url.startsWith('/')) {
        return false;
    }
    try {
        return new URL(url, window.location.href).origin !== window.location.origin;
    } catch {
        return false;
    }
}

/** 卡片封面：缓存优先 + 懒加载视频帧 */
class VideoCardPreview extends React.Component {
    constructor(props) {
        super(props);
        this.containerRef = React.createRef();
        this.videoRef = React.createRef();
        this.releaseSlotRef = null;
        this.releasingRef = false;
        this.networkCancelRef = false;
        this.unsubPause = null;
        this.unsubResume = null;
        this.observer = null;

        const { videoId, updateTime } = props;
        const cacheVersion = updateTime ? String(updateTime) : '0';
        const cachedSrc = getVideoThumb(videoId, cacheVersion);

        this.state = {
            visible: false,
            cachedSrc,
            canLoad: false,
            ready: !!cachedSrc,
            failed: false,
            loadTick: 0,
        };
        this.cacheVersion = cacheVersion;
    }

    getCacheVersion = () => {
        const { updateTime } = this.props;
        return updateTime ? String(updateTime) : '0';
    };

    componentDidMount() {
        this.unsubPause = subscribePreviewPause(this.releaseNetwork);
        this.unsubResume = subscribePreviewResume(this.resumePreview);
        this.bindVisibilityResume();
        this.setupIntersectionObserver();
        this.syncThumbFromProps();
    }

    componentDidUpdate(prevProps, prevState) {
        const cacheChanged =
            prevProps.videoId !== this.props.videoId ||
            prevProps.updateTime !== this.props.updateTime;
        if (cacheChanged) {
            this.cacheVersion = this.getCacheVersion();
            this.syncThumbFromProps();
        }
        if (prevProps.disabled !== this.props.disabled) {
            if (this.props.disabled) {
                this.teardownObserver();
            } else {
                this.setupIntersectionObserver();
            }
        }
        if (
            prevState.visible !== this.state.visible ||
            prevState.loadTick !== this.state.loadTick ||
            cacheChanged ||
            prevProps.disabled !== this.props.disabled
        ) {
            this.maybeStartNetworkLoad();
        }
        if (
            prevState.visible !== this.state.visible &&
            this.state.visible &&
            !this.props.disabled
        ) {
            this.bindVisibilityResume();
        }
    }

    componentWillUnmount() {
        this.teardownObserver();
        this.cancelNetworkLoad();
        if (this.unsubPause) this.unsubPause();
        if (this.unsubResume) this.unsubResume();
        this.teardownVisibilityResume();
    }

    syncThumbFromProps = () => {
        const { videoId } = this.props;
        const cacheVersion = this.getCacheVersion();
        const thumb = getVideoThumb(videoId, cacheVersion);
        this.setState({
            cachedSrc: thumb,
            ready: !!thumb,
            failed: false,
            canLoad: false,
        });
    };

    setupIntersectionObserver = () => {
        const { disabled } = this.props;
        if (disabled || this.observer) return;
        const node = this.containerRef.current;
        if (!node) return;

        this.observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    this.setState({ visible: true }, () => {
                        this.observer?.disconnect();
                        this.observer = null;
                    });
                }
            },
            { rootMargin: '80px' }
        );
        this.observer.observe(node);
    };

    teardownObserver = () => {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
    };

    bindVisibilityResume = () => {
        this.teardownVisibilityResume();
        this.onFocusResume = () => this.resumeIfNeeded();
        window.addEventListener('focus', this.onFocusResume);
        document.addEventListener('visibilitychange', this.onFocusResume);
    };

    teardownVisibilityResume = () => {
        if (this.onFocusResume) {
            window.removeEventListener('focus', this.onFocusResume);
            document.removeEventListener('visibilitychange', this.onFocusResume);
            this.onFocusResume = null;
        }
    };

    resumeIfNeeded = () => {
        const { disabled, videoId } = this.props;
        if (disabled || !this.state.visible) return;
        if (document.visibilityState !== 'visible') return;
        const cacheVersion = this.getCacheVersion();
        const thumb = getVideoThumb(videoId, cacheVersion);
        if (thumb) {
            this.setState({ cachedSrc: thumb, ready: true, failed: false });
            return;
        }
        const { canLoad, ready, failed } = this.state;
        if (!canLoad && !ready && !failed) {
            this.setState((s) => ({ loadTick: s.loadTick + 1 }));
        }
    };

    releaseNetwork = () => {
        const { videoId } = this.props;
        const cacheVersion = this.getCacheVersion();
        this.releasingRef = true;
        const storageThumb = getVideoThumb(videoId, cacheVersion);
        this.setState({ canLoad: false });
        const el = this.videoRef.current;
        if (el) {
            el.pause();
            el.removeAttribute('src');
            el.load();
        }
        if (this.releaseSlotRef) {
            this.releaseSlotRef();
            this.releaseSlotRef = null;
        }
        if (storageThumb) {
            this.setState({
                cachedSrc: storageThumb,
                ready: true,
                failed: false,
                canLoad: false,
            });
        } else {
            this.setState({ ready: false });
        }
        requestAnimationFrame(() => {
            this.releasingRef = false;
        });
    };

    resumePreview = () => {
        const { videoId, disabled } = this.props;
        const cacheVersion = this.getCacheVersion();
        const thumb = getVideoThumb(videoId, cacheVersion);
        if (thumb) {
            this.setState({
                cachedSrc: thumb,
                ready: true,
                failed: false,
                canLoad: false,
            });
            return;
        }
        if (this.state.visible && !disabled) {
            this.setState((s) => ({ failed: false, loadTick: s.loadTick + 1 }));
        }
    };

    cancelNetworkLoad = () => {
        this.networkCancelRef = true;
        if (this.releaseSlotRef) {
            this.releaseSlotRef();
            this.releaseSlotRef = null;
        }
        this.setState({ canLoad: false });
    };

    maybeStartNetworkLoad = () => {
        const { videoId, disabled } = this.props;
        const { visible } = this.state;
        if (!visible || disabled) return;

        const cacheVersion = this.getCacheVersion();
        if (getVideoThumb(videoId, cacheVersion)) {
            this.setState({ ready: true });
            return;
        }

        this.networkCancelRef = false;
        const start = async () => {
            const release = await acquirePreviewSlot();
            if (this.networkCancelRef) {
                release();
                return;
            }
            this.releaseSlotRef = release;
            this.setState({ canLoad: true });
        };
        start();
    };

    persistFrame = () => {
        const { videoId, staleMissing, onPreviewVerified } = this.props;
        const cacheVersion = this.getCacheVersion();
        const el = this.videoRef.current;
        const dataUrl = captureVideoFrame(el);
        if (dataUrl) {
            setVideoThumb(videoId, cacheVersion, dataUrl);
            this.setState({
                cachedSrc: dataUrl,
                ready: true,
                canLoad: false,
            });
            if (this.releaseSlotRef) {
                this.releaseSlotRef();
                this.releaseSlotRef = null;
            }
            if (staleMissing && onPreviewVerified) {
                onPreviewVerified(videoId);
            }
            return;
        }
        this.setState({ ready: true });
        if (staleMissing && onPreviewVerified) {
            onPreviewVerified(videoId);
        }
    };

    seekPreviewFrame = () => {
        const el = this.videoRef.current;
        if (!el) {
            this.setState({ ready: true });
            return;
        }
        if (!Number.isFinite(el.duration) || el.duration <= 0) {
            this.persistFrame();
            return;
        }
        const target = Math.min(PREVIEW_SEEK_SEC, Math.max(0, el.duration - 0.1));
        if (el.readyState >= 2 && Math.abs(el.currentTime - target) < 0.05) {
            this.persistFrame();
            return;
        }
        const onSeeked = () => {
            el.removeEventListener('seeked', onSeeked);
            this.persistFrame();
        };
        el.addEventListener('seeked', onSeeked);
        try {
            el.currentTime = target;
        } catch {
            this.persistFrame();
        }
    };

    handleLoadedData = () => {
        this.seekPreviewFrame();
    };

    handleError = () => {
        if (this.releasingRef) {
            return;
        }
        const updates = { failed: true };
        if (!this.state.cachedSrc) {
            updates.ready = false;
        }
        this.setState(updates);
        if (this.releaseSlotRef) {
            this.releaseSlotRef();
            this.releaseSlotRef = null;
        }
    };

    render() {
        const { videoId, fileExt, disabled, staleMissing } = this.props;
        const { cachedSrc, canLoad, ready, failed, visible } = this.state;

        const ext = (fileExt || '').toLowerCase();
        const playUrl = canLoad ? getVideoPreviewUrl(videoId) : '';
        const showCachedImg = !!cachedSrc && ready;
        const showVideoThumb = canLoad && ready && !failed && !showCachedImg;
        const hidePlaceholder = showCachedImg || showVideoThumb;
        const showPlaceholder = disabled || !visible || !hidePlaceholder;
        const showLoading = visible && !disabled && !failed && !ready && !showCachedImg;

        return (
            <div ref={this.containerRef} className="video-card-preview">
                <span
                    className={`video-card-preview__placeholder${hidePlaceholder ? ' video-card-preview__placeholder--hidden' : ''}`}
                    data-ext={ext}
                >
                    <FileOutlined className="video-card-preview__icon" />
                </span>

                {showCachedImg && (
                    <img
                        src={cachedSrc}
                        alt=""
                        className="video-card-preview__video video-card-preview__video--ready"
                    />
                )}

                {showLoading && (
                    <div className="video-card-preview__loading">
                        <Spin size="small" />
                    </div>
                )}

                {canLoad && !disabled && !failed && playUrl && (
                    <video
                        ref={this.videoRef}
                        className={`video-card-preview__video${showVideoThumb || (ready && !showCachedImg) ? ' video-card-preview__video--ready' : ''}${showCachedImg ? ' video-card-preview__video--capture' : ''}`}
                        src={playUrl}
                        crossOrigin={needsCrossOrigin(playUrl) ? 'anonymous' : undefined}
                        preload="auto"
                        muted
                        playsInline
                        onLoadedData={this.handleLoadedData}
                        onError={this.handleError}
                    />
                )}

                {showPlaceholder && failed && !cachedSrc && (
                    <span className="video-card-preview__fallback-hint">
                        {staleMissing ? '磁盘文件缺失' : '预览不可用'}
                    </span>
                )}
            </div>
        );
    }
}

export default VideoCardPreview;
